import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import axios from "../../api/axios";
import { useNavigate } from "react-router-dom";
import { getUserFromStorage } from "../../utils/auth";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import usePageTitle from "../../utils/usePageTitle";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  Legend,
  CartesianGrid
} from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Papa from "papaparse";
import RefreshIcon from "@mui/icons-material/Refresh";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

dayjs.locale("fr");

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

const PERIODS = [
  { value: "daily", label: "Jour" },
  { value: "weekly", label: "Semaine" },
  { value: "monthly", label: "Mois" },
  { value: "yearly", label: "Année" },
  { value: "custom", label: "Personnalisé" }
];

const TIMEZONE_OPTIONS = [
  { value: "Africa/Kinshasa", label: "Kinshasa" },
  { value: "Asia/Shanghai", label: "Chine" },
  { value: "UTC", label: "UTC" }
];

const DEFAULT_TIMEZONE = "Africa/Kinshasa";

function fmtCurrency(value, currency) {
  const n = Number(value || 0);

  if (!currency || currency === "ALL") {
    return new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  }

  try {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency, maximumFractionDigits: 2 }).format(n);
  } catch (e) {
    return `${n.toFixed(2)} ${currency}`;
  }
}

const AMOUNT_COLOR = "#1976d2";
const FEE_COLOR = "#424242";
const PDF_AMOUNT_RGB = [25, 118, 210];
const PDF_COMMISSION_RGB = [60, 60, 60];

function monthNameFRUpper(d) {
  const m = dayjs(d).month();
  return String(MONTHS_FR[m] || "").toUpperCase()
    .replace(/É|È|Ê/g, "E")
    .replace(/À/g, "A")
    .replace(/Û/g, "U")
    .replace(/Ô/g, "O")
    .replace(/Ï|Î/g, "I")
    .replace(/\s+/g, "_");
}

function shortId(len = 4) {
  try {
    const arr = new Uint8Array(len);
    window.crypto.getRandomValues(arr);
    return Array.from(arr).map((b) => (b % 36).toString(36)).join("").toUpperCase();
  } catch {
    return Math.random().toString(36).slice(2, 2 + len).toUpperCase();
  }
}

function buildShortReportName({ period, startDate, endDate }) {
  const year = dayjs().format("YYYY");
  const month = monthNameFRUpper(dayjs());
  const uniq = shortId(4);

  let periodPart = period.toUpperCase();
  if (period === "custom" && startDate && endDate) {
    periodPart = `DU_${dayjs(startDate).format("YYYYMMDD")}_AU_${dayjs(endDate).format("YYYYMMDD")}`;
  }

  return `RAPPORT_TRANSACTIONS__${year}__${month}__${periodPart}__${uniq}`;
}

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("daily");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [currency, setCurrency] = useState("");
  const currencyOptions = useMemo(() => ["USD", "CDF"], []);

  const [statusFilter, setStatusFilter] = useState("");
  const [agency, setAgency] = useState("");
  const [agencies, setAgencies] = useState([]);

  const [timezoneFilter, setTimezoneFilter] = useState(DEFAULT_TIMEZONE);

  const [senderLocation, setSenderLocation] = useState("");
  const [receiverLocation, setReceiverLocation] = useState("");
  const [locations, setLocations] = useState([]);

  const [stats, setStats] = useState(null);

  const debounceRef = useRef(null);
  const chartRef = useRef(null);

  usePageTitle("Tableau de bord");

  useEffect(() => {
    const s = getUserFromStorage();
    if (!s || s.role !== "admin") {
      navigate("/login");
      return;
    }

    (async () => {
      try {
        const token = localStorage.getItem("token");

        const [locRes, agencyRes] = await Promise.all([
          axios.get("/settings/locations", { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          axios.get("/agencies", { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
        ]);

        if (locRes && Array.isArray(locRes.data)) {
          setLocations(locRes.data);
        }

        if (agencyRes && Array.isArray(agencyRes.data)) {
          setAgencies(agencyRes.data);
        }
      } catch (err) {
        console.error("initial load error", err);
      }
    })();
  }, [navigate]);

  const handlePeriod = (_, val) => {
    if (!val) return;
    setPeriod(val);

    if (val === "custom") {
      setStats(null);
      return;
    }

    setStartDate(null);
    setEndDate(null);
  };

  const getLocationLabel = useCallback(
    (value) => {
      if (!value) return "Tous";
      const num = Number(value);
      if (!Number.isNaN(num)) {
        const found = (locations || []).find((l) => Number(l.id) === num);
        if (found) return found.city ? `${found.city}, ${found.country}` : (found.location || String(value));
      }
      return String(value);
    },
    [locations]
  );

  const getAgencyLabel = useCallback(
    (value) => {
      if (!value) return "Toutes";
      const found = (agencies || []).find((a) => Number(a.id) === Number(value));
      return found?.name || String(value);
    },
    [agencies]
  );

 

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = { period, timezone: timezoneFilter || DEFAULT_TIMEZONE };

      if (period === "custom") {
        if (!startDate || !endDate) {
          setStats(null);
          setLoading(false);
          return;
        }
        params.startDate = dayjs(startDate).format("YYYY-MM-DD");
        params.endDate = dayjs(endDate).format("YYYY-MM-DD");
      }

      if (agency) params.agency = Number(agency);
      if (senderLocation) params.senderLocation = Number(senderLocation);
      if (receiverLocation) params.receiverLocation = Number(receiverLocation);
      if (statusFilter) params.status = statusFilter;
      if (currency) params.currency = currency;

      const res = await axios.get("/admin/stats", {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setStats(res.data || null);
    } catch (err) {
      console.error("fetchStats error:", err);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [period, startDate, endDate, agency, senderLocation, receiverLocation, statusFilter, currency, timezoneFilter]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (period === "custom" && (!startDate || !endDate)) return;

    debounceRef.current = setTimeout(() => {
      fetchStats();
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [period, startDate, endDate, agency, senderLocation, receiverLocation, statusFilter, currency, timezoneFilter, fetchStats]);

  const isAllCurrencies = useMemo(() => stats?.currency === "ALL", [stats]);
  const customIncomplete = period === "custom" && (!startDate || !endDate);

  const hasData = useMemo(() => {
    if (!stats) return false;
    if (stats?.hasData === true) return true;
    const tx = Number(stats?.summary?.transactionCount || 0);
    return tx > 0;
  }, [stats]);

  const getYm = useCallback((row) => {
    if (!row) return null;
    if (row.ym) return String(row.ym);
    if (row.year != null && row.month != null) {
      return `${Number(row.year)}-${String(Number(row.month)).padStart(2, "0")}`;
    }
    if (row.date) {
      const d = dayjs(row.date, "YYYY-MM-DD", true);
      if (d.isValid()) return d.format("YYYY-MM");
    }
    if (row.date && dayjs(row.date).isValid()) {
      return dayjs(row.date).format("YYYY-MM");
    }
    return null;
  }, []);

  const labelFromYm = useCallback((ym) => {
    if (!ym) return "";
    const [y, m] = String(ym).split("-");
    const mi = Number(m) - 1;
    if (mi < 0 || mi > 11) return ym;
    return `${MONTHS_FR[mi]} ${y}`;
  }, []);

  const labelForRow = useCallback(
    (row, currentPeriod) => {
      if (currentPeriod === "yearly") {
        return labelFromYm(getYm(row)) || row?.label || "";
      }

      if (currentPeriod === "weekly" && row?.date) {
        const d = dayjs(row.date, "YYYY-MM-DD", true);
        if (d.isValid()) return d.format("dddd");
      }

      if (row?.date) {
        const d = dayjs(row.date, "YYYY-MM-DD", true);
        if (d.isValid()) return d.format("DD/MM/YYYY");
      }

      return row?.label || (getYm(row) || "");
    },
    [getYm, labelFromYm]
  );

  const normalized = useMemo(() => {
    if (!stats || !hasData) return { details: [], chart: [] };

    const showAll = stats.currency === "ALL";

    const arr = (...cands) => {
      for (const c of cands) if (Array.isArray(c) && c.length) return c;
      for (const c of cands) if (Array.isArray(c)) return c;
      return [];
    };

    const grouped = arr(stats.grouped);
    const groupedByCurrency = arr(stats.groupedByCurrency);

    const rawChart = grouped;

    let rawDetails = [];
    if (showAll) rawDetails = groupedByCurrency.length ? groupedByCurrency : [];
    else rawDetails = grouped.length ? grouped : [];

    let effectiveDetailsSource = rawDetails;
    if (!effectiveDetailsSource.length && grouped.length) effectiveDetailsSource = grouped;

    const n = (v) => Number(v || 0);

    const isBogusRow = (r) => {
      const cur = String(r?.currency || "").toUpperCase();
      const label = String(r?.label || "").toUpperCase();
      const isUnknown = cur === "UNKNOWN" || label === "UNKNOWN";
      const isZero = n(r?.count) === 0 && n(r?.amount) === 0 && n(r?.fee) === 0;
      return isUnknown && isZero;
    };

    if (period === "yearly") {
      const map = new Map();

      for (const r of effectiveDetailsSource) {
        const ym = getYm(r);
        if (!ym) continue;

        const cur = showAll ? (r.currency || "UNKNOWN") : (stats.currency || currency || "USD");
        const key = showAll ? `${cur}|${ym}` : ym;

        const prev = map.get(key) || { ym, currency: cur, count: 0, amount: 0, fee: 0 };
        prev.count += n(r.count);
        prev.amount += n(r.amount);
        prev.fee += n(r.fee);
        map.set(key, prev);
      }

      let rows = Array.from(map.values()).sort((a, b) => {
        if (showAll && a.currency !== b.currency) return String(a.currency).localeCompare(String(b.currency));
        return String(a.ym).localeCompare(String(b.ym));
      });

      if (!showAll) rows = rows.slice(-12);
      else {
        const byCur = new Map();
        for (const r of rows) {
          const list = byCur.get(r.currency) || [];
          list.push(r);
          byCur.set(r.currency, list);
        }
        rows = [];
        for (const [, list] of byCur.entries()) rows.push(...list.slice(-12));
        rows.sort(
          (a, b) =>
            String(a.currency).localeCompare(String(b.currency)) ||
            String(a.ym).localeCompare(String(b.ym))
        );
      }

      let details = rows.map((r) => ({
        label: labelFromYm(r.ym),
        currency: r.currency,
        count: Number(r.count || 0),
        amount: Number(r.amount || 0),
        fee: Number(r.fee || 0),
        amountFormatted: fmtCurrency(r.amount || 0, r.currency),
        feeFormatted: fmtCurrency(r.fee || 0, r.currency)
      }));

      details = details.filter((r) => !isBogusRow(r));

      const chartMap = new Map();
      for (const r of rawChart) {
        const ym = getYm(r);
        if (!ym) continue;

        const prev = chartMap.get(ym) || { ym, count: 0, amount: 0, fee: 0 };
        prev.count += n(r.count);
        prev.amount += n(r.amount);
        prev.fee += n(r.fee);
        chartMap.set(ym, prev);
      }

      const chart = Array.from(chartMap.values())
        .sort((a, b) => String(a.ym).localeCompare(String(b.ym)))
        .slice(-12)
        .map((r) => ({
          name: labelFromYm(r.ym),
          count: r.count,
          amount: showAll ? null : r.amount,
          fee: showAll ? null : r.fee
        }))
        .filter((r) => n(r.count) !== 0 || n(r.amount) !== 0 || n(r.fee) !== 0);

      return { details, chart };
    }

    let details = (effectiveDetailsSource || []).map((r) => {
      const rowCurrency = showAll ? (r.currency || "UNKNOWN") : (stats.currency || currency || "USD");
      const amount = n(r.amount);
      const fee = n(r.fee);

      return {
        label: labelForRow(r, period),
        currency: rowCurrency,
        count: n(r.count),
        amount,
        fee,
        amountFormatted: fmtCurrency(amount, rowCurrency),
        feeFormatted: fmtCurrency(fee, rowCurrency)
      };
    });

    details = details.filter((r) => !isBogusRow(r));

    const chart = (rawChart || [])
      .map((r) => ({
        name: labelForRow(r, period),
        count: n(r.count),
        amount: showAll ? null : n(r.amount),
        fee: showAll ? null : n(r.fee)
      }))
      .filter((r) => n(r.count) !== 0 || n(r.amount) !== 0 || n(r.fee) !== 0);

    return { details, chart };
  }, [stats, hasData, period, currency, getYm, labelFromYm, labelForRow]);

  const detailsList = normalized.details;
  const chartData = normalized.chart;
  const chartHasData = chartData.length > 0;

  const growthAmountPercent = useMemo(() => {
    if (!stats || isAllCurrencies) return null;
    const backendPercent = stats?.growth?.amountPercent;
    if (backendPercent !== undefined && backendPercent !== null) return backendPercent;

    const prevAmount = stats?.previous?.totalAmount ?? 0;
    const curAmount = stats?.summary?.totalAmount ?? 0;
    if (!prevAmount || prevAmount === 0) return 0;
    return Number((((curAmount - prevAmount) / prevAmount) * 100).toFixed(2));
  }, [stats, isAllCurrencies]);

  const exportCSV = () => {
    if (!detailsList.length || !stats) return;

    const rows = detailsList.map((r) => ({
      Periode: r.label,
      Agence: getAgencyLabel(agency),
      Fuseau: stats?.tz || timezoneFilter || DEFAULT_TIMEZONE,
      Devise: isAllCurrencies ? r.currency : (stats.currency || currency || "USD"),
      Transactions: r.count,
      Montant: r.amount,
      Commission: r.fee,
      "Montant (formaté)": r.amountFormatted,
      "Commission (formaté)": r.feeFormatted
    }));

    const baseName = buildShortReportName({ period, startDate, endDate });
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    if (!stats) return;

    const baseName = buildShortReportName({ period, startDate, endDate });

    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 32;

    const pdfSafe = (s) =>
      String(s ?? "")
        .replace(/[\u00A0\u202F]/g, " ")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .trim();

    const title = "Rapport des Transactions";
    const generatedAt = dayjs().format("DD/MM/YYYY HH:mm");

    const periodLabel =
      period === "custom" && startDate && endDate
        ? `${dayjs(startDate).format("DD MMM YYYY")} — ${dayjs(endDate).format("DD MMM YYYY")}`
        : (PERIODS.find(p => p.value === period)?.label || period);

    const effectiveCurrency = stats.currency || (currency ? currency : "ALL");
    const showAll = isAllCurrencies;

    const leftX = margin + 12;
    const maxTextW = pageW - margin * 2 - 24;

    const drawLeftLine = (text, yy, { font = ["helvetica", "normal"], size = 10, color = [0, 0, 0] } = {}) => {
      pdf.setFont(font[0], font[1]);
      pdf.setFontSize(size);
      pdf.setTextColor(...color);
      const lines = pdf.splitTextToSize(pdfSafe(text), maxTextW);
      pdf.text(lines, leftX, yy);
      return yy + (lines.length * (size + 2));
    };

    pdf.setTextColor(0, 0, 0);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text(pdfSafe(title), margin, 42);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(pdfSafe(`Période: ${periodLabel}`), margin, 60);
    pdf.text(pdfSafe(`Devise: ${showAll ? "Tous" : effectiveCurrency}`), margin, 74);
    pdf.text(pdfSafe(`Généré le: ${generatedAt}`), pageW - margin, 74, { align: "right" });

    const sourceLabel = getLocationLabel(senderLocation);
    const destLabel = getLocationLabel(receiverLocation);
    const agencyLabel = getAgencyLabel(agency);

    const cardY = 92;
    const cardH = 58;
    pdf.setDrawColor(220);
    pdf.roundedRect(margin, cardY, pageW - margin * 2, cardH, 8, 8);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text("Filtres", leftX, cardY + 16);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text(pdfSafe(`Agence: ${agencyLabel}`), leftX, cardY + 30);
    pdf.text(pdfSafe(`Fuseau: ${stats?.tz || timezoneFilter || DEFAULT_TIMEZONE}`), margin + 220, cardY + 30);
    pdf.text(pdfSafe(`Statut: ${statusFilter || "Tous"}`), pageW - margin - 12, cardY + 30, { align: "right" });

    pdf.text(pdfSafe(`Source: ${sourceLabel}`), leftX, cardY + 46);
    pdf.text(pdfSafe(`Destination: ${destLabel}`), margin + 220, cardY + 46);

    let y = cardY + cardH + 16;

    const txCount = Number(stats?.summary?.transactionCount || 0);
    const backendHasData = stats?.hasData === true || txCount > 0;
    if (!backendHasData) {
      pdf.setDrawColor(200);
      pdf.roundedRect(margin, y, pageW - margin * 2, 90, 8, 8);

      y = drawLeftLine("Pas de transactions", y + 30, { font: ["helvetica", "bold"], size: 14 });
      drawLeftLine("Aucune donnée pour cette période / ces filtres.", y + 4, {
        font: ["helvetica", "normal"],
        size: 11,
        color: [80, 80, 80]
      });

      pdf.save(`${baseName}.pdf`);
      return;
    }

    const summaryH = showAll ? 180 : 140;
    pdf.setDrawColor(200);
    pdf.roundedRect(margin, y, pageW - margin * 2, summaryH, 8, 8);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    pdf.text("Résumé", leftX, y + 18);

    let yy = y + 40;

    yy = drawLeftLine(`Transactions: ${stats.summary?.transactionCount ?? 0}`, yy, {
      font: ["helvetica", "bold"],
      size: 11
    });

    if (!showAll) {
      const curCur = stats.currency || currency || "USD";
      const totalAmount = fmtCurrency(stats.summary?.totalAmount ?? 0, curCur);
      const totalFee = fmtCurrency(stats.summary?.totalFee ?? 0, curCur);

      yy = drawLeftLine(`Montant total: ${totalAmount}`, yy + 10, {
        font: ["helvetica", "bold"],
        size: 22,
        color: PDF_AMOUNT_RGB
      });

      yy = drawLeftLine(`Commission totale: ${totalFee}`, yy + 6, {
        font: ["helvetica", "bold"],
        size: 22,
        color: PDF_COMMISSION_RGB
      });

      y += summaryH + 18;
    } else {
      const list = Array.isArray(stats.summaryByCurrency) ? stats.summaryByCurrency : [];
      yy = drawLeftLine("Totaux par devise :", yy + 10, { font: ["helvetica", "bold"], size: 11 });

      const maxLines = 3;

      list.slice(0, maxLines).forEach((c, idx) => {
        const cur = pdfSafe(c.currency || "UNKNOWN");
        const amt = pdfSafe(fmtCurrency(c.totalAmount, c.currency));
        const fee = pdfSafe(fmtCurrency(c.totalFee, c.currency));

        yy += idx === 0 ? 6 : 10;

        yy = drawLeftLine(cur, yy, { font: ["helvetica", "bold"], size: 13, color: [0, 0, 0] });

        yy = drawLeftLine(`Montant : ${amt}`, yy + 4, {
          font: ["helvetica", "bold"],
          size: 12,
          color: PDF_AMOUNT_RGB
        });

        yy = drawLeftLine(`Commission : ${fee}`, yy + 2, {
          font: ["helvetica", "bold"],
          size: 12,
          color: PDF_COMMISSION_RGB
        });

        pdf.setDrawColor(230);
        pdf.line(margin + 12, yy + 6, pageW - margin - 12, yy + 6);
        yy += 8;
      });

      if (list.length > maxLines) {
        yy = drawLeftLine(`(+${list.length - maxLines} autre(s) devise(s))`, yy + 6, {
          font: ["helvetica", "normal"],
          size: 9,
          color: [80, 80, 80]
        });
      }

      y += summaryH + 18;
    }

    if (chartRef.current && chartHasData) {
      try {
        const canvas = await html2canvas(chartRef.current, { scale: 2, backgroundColor: "#ffffff" });
        const img = canvas.toDataURL("image/png");

        const maxW = pageW - margin * 2;
        const imgProps = pdf.getImageProperties(img);
        let imgH = (imgProps.height * maxW) / imgProps.width;

        const maxH = 260;
        if (imgH > maxH) imgH = maxH;

        if (y + imgH > pageH - 80) {
          pdf.addPage();
          y = 50;
        }

        pdf.addImage(img, "PNG", margin, y, maxW, imgH);
        y += imgH + 18;
      } catch (err) {
        console.warn("PDF capture failed", err);
      }
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text("Répartition détaillée", margin, y);
    y += 14;

    const headers = showAll
      ? [period === "yearly" ? "Mois" : "Période", "Devise", "Tx", "Montant", "Commission"]
      : [period === "yearly" ? "Mois" : "Période", "Tx", "Montant", "Commission"];

    const colX = showAll
      ? [margin, margin + 240, margin + 310, margin + 400, margin + 500]
      : [margin, margin + 300, margin + 420, margin + 520];

    const drawTableHeader = () => {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);

      pdf.text(headers[0], colX[0], y);

      if (showAll) {
        pdf.text(headers[1], colX[1], y);
        pdf.text(headers[2], colX[2], y, { align: "right" });
        pdf.text(headers[3], colX[3], y, { align: "right" });
        pdf.text(headers[4], colX[4], y, { align: "right" });
      } else {
        pdf.text(headers[1], colX[1], y, { align: "right" });
        pdf.text(headers[2], colX[2], y, { align: "right" });
        pdf.text(headers[3], colX[3], y, { align: "right" });
      }

      y += 10;
      pdf.setDrawColor(220);
      pdf.line(margin, y, pageW - margin, y);
      y += 12;
    };

    drawTableHeader();

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);

    for (const r of detailsList) {
      if (y > pageH - 60) {
        pdf.addPage();
        y = 50;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text(pdfSafe(title), margin, 30);

        pdf.setFont("helvetica", "normal");
        pdf.text(pdfSafe(`Période: ${periodLabel}`), margin, 44);
        pdf.text(pdfSafe(`Généré le: ${generatedAt}`), pageW - margin, 44, { align: "right" });

        y = 70;
        drawTableHeader();
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
      }

      pdf.setTextColor(0, 0, 0);
      pdf.text(pdfSafe(r.label || ""), colX[0], y);

      if (showAll) {
        pdf.text(pdfSafe(r.currency || ""), colX[1], y);
        pdf.text(pdfSafe(String(r.count ?? 0)), colX[2], y, { align: "right" });
        pdf.text(pdfSafe(r.amountFormatted || ""), colX[3], y, { align: "right" });
        pdf.text(pdfSafe(r.feeFormatted || ""), colX[4], y, { align: "right" });
      } else {
        pdf.text(pdfSafe(String(r.count ?? 0)), colX[1], y, { align: "right" });
        pdf.text(pdfSafe(r.amountFormatted || ""), colX[2], y, { align: "right" });
        pdf.text(pdfSafe(r.feeFormatted || ""), colX[3], y, { align: "right" });
      }

      y += 12;
    }

    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.text(pdfSafe(`Page ${i}/${totalPages}`), pageW - margin, pageH - 20, { align: "right" });
    }

    pdf.save(`${baseName}.pdf`);
  };

  const periodLabelUI = useMemo(() => (PERIODS.find(p => p.value === period)?.label || period), [period]);

  return (
    <DashboardLayout role="admin">
      <Box mb={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          flexWrap="wrap"
        >
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriod}
            sx={{
              width: { xs: "100%", md: "auto" },
              "& .MuiToggleButton-root": { flex: { xs: 1, md: "unset" } }
            }}
          >
            {PERIODS.map((p) => (
              <ToggleButton key={p.value} value={p.value}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {period === "custom" && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: { xs: "100%", md: "auto" } }}>
              <DatePicker label="Date de début" value={startDate} onChange={(v) => setStartDate(v ? dayjs(v) : null)} />
              <DatePicker label="Date de fin" value={endDate} onChange={(v) => setEndDate(v ? dayjs(v) : null)} />
            </Stack>
          )}

          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 180 } }}>
            <InputLabel>Agence</InputLabel>
            <Select value={agency} onChange={(e) => setAgency(e.target.value)} label="Agence">
              <MenuItem value="">Toutes</MenuItem>
              {(agencies || []).map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 180 } }}>
            <InputLabel>Fuseau horaire</InputLabel>
            <Select value={timezoneFilter} onChange={(e) => setTimezoneFilter(e.target.value)} label="Fuseau horaire">
              {TIMEZONE_OPTIONS.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 120 } }}>
            <InputLabel>Devise</InputLabel>
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)} label="Devise">
              <MenuItem value="">Tous</MenuItem>
              {currencyOptions.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 220 } }}>
            <InputLabel>Source (envoyeur)</InputLabel>
            <Select
              value={senderLocation}
              onChange={(e) => setSenderLocation(e.target.value)}
              label="Source (envoyeur)"
            >
              <MenuItem value="">Tous</MenuItem>
              {(locations.length ? locations : []).map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.city ? `${l.city}, ${l.country}` : String(l.location || l.id)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 220 } }}>
            <InputLabel>Destination (bénéficiaire)</InputLabel>
            <Select
              value={receiverLocation}
              onChange={(e) => setReceiverLocation(e.target.value)}
              label="Destination (bénéficiaire)"
            >
              <MenuItem value="">Tous</MenuItem>
              {(locations.length ? locations : []).map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.city ? `${l.city}, ${l.country}` : String(l.location || l.id)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 160 } }}>
            <InputLabel>Statut</InputLabel>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Statut">
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="pending">pending</MenuItem>
              <MenuItem value="paid">paid</MenuItem>
              <MenuItem value="cancelled">cancelled</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: "flex-end", md: "flex-start" } }}>
            <Tooltip title="Rafraîchir">
              <IconButton color="primary" onClick={() => fetchStats()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Exporter CSV">
              <IconButton color="secondary" onClick={exportCSV} disabled={!detailsList.length || !stats || !hasData}>
                <GetAppIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Exporter PDF">
              <IconButton color="secondary" onClick={exportPDF} disabled={!stats}>
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Box mt={1}>
          <Typography variant="caption" color="text.secondary">
            Fuseau utilisé pour le rapport : <b>{stats?.tz || timezoneFilter || DEFAULT_TIMEZONE}</b>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ bgcolor: "#fff", p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        {loading && !stats ? (
          <Box textAlign="center" py={6}>
            <CircularProgress />
          </Box>
        ) : customIncomplete ? (
          <Box mt={1} p={2.5} sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 3 }}>
            <Typography variant="body2">
              Veuillez sélectionner une <b>date de début</b> et une <b>date de fin</b> pour afficher le rapport personnalisé.
            </Typography>
          </Box>
        ) : !hasData ? (
          <Box
            mt={1}
            p={{ xs: 2.5, md: 4 }}
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 3,
              textAlign: "center"
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.75 }}>
              Pas de transactions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aucune donnée pour cette période / ces filtres.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Astuce: essayez une autre période ({periodLabelUI}) ou retirez un filtre (agence/fuseau/source/destination/statut/devise).
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary">
                      Total Montant
                    </Typography>

                    {isAllCurrencies && Array.isArray(stats?.summaryByCurrency) ? (
                      <Box mt={1}>
                        {stats.summaryByCurrency.map((c) => (
                          <Box
                            key={c.currency}
                            sx={{
                              py: 0.75,
                              borderBottom: "1px dashed",
                              borderColor: "divider",
                              "&:last-of-type": { borderBottom: "none" }
                            }}
                          >
                            <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                {c.currency}
                              </Typography>

                              <Typography
                                sx={{
                                  fontWeight: 900,
                                  fontSize: { xs: 18, md: 20 },
                                  color: AMOUNT_COLOR,
                                  lineHeight: 1.1
                                }}
                              >
                                {fmtCurrency(c.totalAmount, c.currency)}
                              </Typography>
                            </Stack>

                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                              Transactions: <b>{c.transactionCount ?? 0}</b>
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <>
                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: { xs: 28, md: 40 },
                            color: AMOUNT_COLOR,
                            lineHeight: 1.1,
                            letterSpacing: 0.2
                          }}
                        >
                          {fmtCurrency(stats?.summary?.totalAmount ?? 0, stats?.currency || currency || "USD")}
                        </Typography>
                        <Typography variant="caption">
                          Transactions: {stats?.summary?.transactionCount ?? "-"}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary">
                      Total Commission
                    </Typography>

                    {isAllCurrencies && Array.isArray(stats?.summaryByCurrency) ? (
                      <Box mt={1}>
                        {stats.summaryByCurrency.map((c) => (
                          <Box
                            key={c.currency}
                            sx={{
                              py: 0.75,
                              borderBottom: "1px dashed",
                              borderColor: "divider",
                              "&:last-of-type": { borderBottom: "none" }
                            }}
                          >
                            <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                {c.currency}
                              </Typography>

                              <Typography
                                sx={{
                                  fontWeight: 900,
                                  fontSize: { xs: 18, md: 20 },
                                  color: FEE_COLOR,
                                  lineHeight: 1.1
                                }}
                              >
                                {fmtCurrency(c.totalFee, c.currency)}
                              </Typography>
                            </Stack>

                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                              Transactions: <b>{c.transactionCount ?? 0}</b>
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <>
                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: { xs: 28, md: 40 },
                            color: FEE_COLOR,
                            lineHeight: 1.1,
                            letterSpacing: 0.2
                          }}
                        >
                          {fmtCurrency(stats?.summary?.totalFee ?? 0, stats?.currency || currency || "USD")}
                        </Typography>
                        <Typography variant="caption">
                          Croissance: {stats?.growth?.feePercent ?? "N/A"}%
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary">
                      Meilleur (période)
                    </Typography>
                    <Typography variant="h6">
                      {stats?.bestDay
                        ? isAllCurrencies
                          ? `${stats.bestDay.label ?? ""} — ${stats.bestDay.count ?? 0} tx`
                          : `${stats.bestDay.label ?? ""} — ${fmtCurrency(stats.bestDay.amount, stats.currency || currency || "USD")}`
                        : "N/A"}
                    </Typography>
                    <Typography variant="caption">{stats?.bestDay ? `${stats.bestDay.count} tx` : ""}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary">
                      Croissance
                    </Typography>
                    <Typography variant="h6">{stats?.growth?.transactionsPercent ?? "N/A"}%</Typography>
                    <Typography variant="caption">
                      {isAllCurrencies ? "Montant: N/A (multi-devise)" : `Montant: ${growthAmountPercent ?? "N/A"}%`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {chartHasData && (
              <Box mt={4} ref={chartRef}>
                <Typography variant="h6" gutterBottom>
                  Transactions (bar){isAllCurrencies ? "" : " · Montant & Commission (lignes)"}
                </Typography>

                <ResponsiveContainer width="100%" height={360}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" interval={0} tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" />
                    {!isAllCurrencies && <YAxis yAxisId="right" orientation="right" />}
                    <ReTooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" name="Transactions" fill="#4caf50" />
                    {!isAllCurrencies && (
                      <>
                        <Line yAxisId="right" type="monotone" dataKey="amount" stroke="#1976d2" name="Montant" strokeWidth={2} dot={false} />
                        <Line yAxisId="right" type="monotone" dataKey="fee" stroke="#ff9800" name="Commission" strokeWidth={2} dot={false} />
                      </>
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}

            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Répartition détaillée
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{period === "yearly" ? "Mois" : "Période"}</TableCell>
                    {isAllCurrencies && <TableCell align="right">Devise</TableCell>}
                    <TableCell align="right">Transactions</TableCell>
                    <TableCell align="right">Montant</TableCell>
                    <TableCell align="right">Commission</TableCell>
                    <TableCell align="right">Détails</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {detailsList.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ textTransform: period === "weekly" ? "capitalize" : "none" }}>
                        {r.label}
                      </TableCell>
                      {isAllCurrencies && <TableCell align="right">{r.currency}</TableCell>}
                      <TableCell align="right">{r.count}</TableCell>
                      <TableCell align="right">{r.amountFormatted}</TableCell>
                      <TableCell align="right">{r.feeFormatted}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Chip label={`${r.count} tx`} size="small" />
                          <Chip label={r.amountFormatted} size="small" color="primary" />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!detailsList.length && (
                    <TableRow>
                      <TableCell colSpan={isAllCurrencies ? 6 : 5} align="center">
                        Aucune donnée pour cette période / filtres.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
}